/**
 * `person:<id>` and its jobs, the person record as a visit (O10, CASE-027).
 * The record is its own editor: the Manage row first, then the Person, Access
 * (with Change role), and the role sections. `/edit` and `/role` open that
 * record with their section and work open; `/access` brings Access and its
 * work only; the lifecycle jobs open their own focused Task.
 *
 * The registrar's record is read once (R1): the person, access, the student
 * record and the teaching profile are sections of it. A viewer outside the
 * registrar shell, or an address no person in this institution answers, is
 * refused and named nothing (R1, P4); Faculty brings its own reading in A5.2.
 */
import { UserRound, ShieldCheck, BookOpen } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router";

import type { Box, FactBox, Read } from "@/components/ui/box.schema";
import { TeachingSummary } from "@/components/faculty/TeachingSummary";
import { ApiError } from "@/lib/api/client";
import { currentOnConflict } from "@/lib/api/conflict";
import { PEOPLE_QUERY_KEY, peopleApi, type PeopleDetailResponse, type PeoplePerson, type PersonFact } from "@/lib/api/people";
import { HONORIFICS } from "@/lib/honorifics";
import type { VisitLevel } from "@/lib/visit/address";
import { shellOf, type VisitResolution } from "@/lib/visit/registry";

import { peopleText as text, peopleWords as words } from "../people-copy";
import { personDisplayName, relationshipsLabel } from "../person-record";
import { accessSummary, useAccessBoxes } from "./access-boxes";
import { imam, queryRead, readOf, useFieldSaves } from "./commit";
import { savedField, type FieldSpec } from "./fields";
import { studentCopy as studentWords } from "./student-copy";
import { useStudentBoxes } from "./student-boxes";
import { useTimeAllowanceBoxes } from "./time-allowance-boxes";
import { useTeachingBoxes } from "./teaching-boxes";
import { teachingCopy, teachingText } from "./teaching-copy";
import { useManagementBoxes, type PersonManagementJob } from "./management-boxes";

const PERSON_FIELDS: readonly PersonFact[] = ["full_name", "preferred_name", "honorific", "email"];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Lifecycle jobs open their own focused Task; edit and role open the record with their work in place. */
const FOCUSED: readonly PersonManagementJob[] = ["archive", "offboard", "delete", "teaching"];
const hostedTask = (box: Box): Box => box.kind === "task" ? { ...box, presentation: "embedded", footer: "host" } : box;

export const personDetailKey = (personId: string | null) => [...PEOPLE_QUERY_KEY, "detail", personId, null] as const;

/** The registrar's read of one person: the record every person box shares. */
export function usePersonDetail(personId: string | null) {
  return useQuery({
    queryKey: personDetailKey(personId),
    queryFn: () => peopleApi.detail(personId as string),
    enabled: personId !== null,
    retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
  });
}

/** The Person Fact (P08): each fact saves in its own field, and a newer version waits at the field (CASE-013, R4). */
function usePersonFact(personId: string | null, read: Read<PeopleDetailResponse>): Box | null {
  const client = useQueryClient();
  const detail = read.status === "ready" ? read.value : read.status === "stale" ? read.last : null;
  const saves = useFieldSaves<PeoplePerson, PersonFact>({
    record: personId ? (detail?.person ?? null) : null,
    identity: (person) => person.id,
    version: (person) => person.updated_at,
    fields: PERSON_FIELDS,
    read: (person, field) => person[field] ?? "",
    send: async (field, value, base, key) =>
      (await peopleApi.patchPerson(base.id, { [field]: value.trim() === "" ? null : value, expected_updated_at: base.updated_at }, key)).person,
    conflictOf: (error) => currentOnConflict<PeoplePerson>(error),
    reread: async () => (await peopleApi.detail(personId as string)).person,
    onSaved: (saved) => {
      // The saved person on every People read that holds it, then a fresh read of the rest.
      client.setQueriesData<PeopleDetailResponse>({ queryKey: [...PEOPLE_QUERY_KEY, "detail"] }, (current) =>
        current && current.person.id === saved.id ? { ...current, person: saved, display_name: saved.full_name, email: saved.email } : current,
      );
      void Promise.all([
        client.invalidateQueries({ queryKey: [...PEOPLE_QUERY_KEY, "register"] }),
        client.invalidateQueries({ queryKey: ["students"] }),
        client.invalidateQueries({ queryKey: ["faculty"] }),
        client.invalidateQueries({ queryKey: ["accounts"] }),
        client.invalidateQueries({ queryKey: ["course-roster"] }),
        client.invalidateQueries({ queryKey: ["course-roster-candidates"] }),
      ]);
    },
  });
  if (!personId) return null;
  const field = (name: PersonFact, label: string, extra: Partial<FieldSpec<PeoplePerson, PersonFact>> = {}) =>
    savedField(saves, { id: `person-${personId}-${name}`, field: name, label, theirs: (theirs) => theirs[name] ?? "", ...extra });
  const person: FactBox<PeoplePerson> = {
    id: `person-${personId}`,
    kind: "fact",
    job: words.personJob,
    label: words.personLabel,
    owner: imam,
    fold: "choice",
    read: read.status === "failed"
      ? { ...read, sentence: words.personUnavailable, detail: words.personUnavailableDetail }
      : readOf(read, (value) => value.person),
    detail: () => words.personDetail,
    prose: (value) => `${words.email}: ${value.email || words.notRecorded}`,
    act: { does: "open", verb: "Edit details" },
    fields: () => [
      field("full_name", words.fullName),
      field("preferred_name", words.preferredName),
      field("honorific", words.honorific, { options: { empty: words.none, choices: HONORIFICS.map((each) => ({ value: each, label: each })) } }),
      field("email", words.email, { type: "email", note: `${detail?.faculty_profile ? `${teachingCopy.profileDetail} ` : "Contact address for this person. "}Changing it does not change their sign-in account.` }),
    ],
  };
  return person as unknown as Box;
}

export function usePersonVisit(level: VisitLevel | null): VisitResolution | null {
  const location = useLocation();
  const shell = shellOf(location.pathname);
  const registrar = shell === "admin" || shell === "adminTeaching";
  const personId = level && registrar && UUID.test(level.id) ? level.id : null;
  const query = usePersonDetail(personId);
  const read = queryRead(query, words.personUnavailable, words.personUnavailableDetail);

  // Every hook runs on every render, in this order, whether or not this level is drawn.
  const person = usePersonFact(personId, read);
  const access = useAccessBoxes(personId, read);
  const studentRecord = useStudentBoxes(personId, read);
  const allowances = useTimeAllowanceBoxes(personId, read);
  const teaching = useTeachingBoxes(personId, read);
  const studentSummary = query.data?.student_record;
  const lead = studentSummary ? <dl className="flex min-w-0 flex-wrap gap-x-8 gap-y-3 text-ui" aria-label="Student placement">
    <div><dt className="text-meta text-muted-foreground">{studentWords.studentId}</dt><dd className="font-mono font-semibold">{studentSummary.sid}</dd></div>
    <div><dt className="text-meta text-muted-foreground">{studentWords.program}</dt><dd>{studentSummary.program_name || studentWords.unassigned}</dd></div>
    <div><dt className="text-meta text-muted-foreground">{studentWords.cohort}</dt><dd>{studentSummary.cohort_name || studentWords.unassigned}</dd></div>
  </dl> : query.data?.faculty_profile ? <TeachingSummary title={query.data.faculty_profile.title_role} specialization={query.data.faculty_profile.specialization} showEmpty /> : undefined;
  const management = useManagementBoxes(personId, read, level?.job ?? null, lead);

  if (!level) return null;
  if (!personId) return { status: "refused" };
  if (query.error instanceof ApiError && (query.error.status === 404 || query.error.status === 403)) return { status: "refused" };
  const detail = query.data;
  if (detail && detail.person.id !== personId) return { status: "refused" };

  // Until the record is read, its Person and Access say so in their own boxes, as a failed read does (R3).
  const subject = detail ? text.subject(personDisplayName(detail), relationshipsLabel(detail, ", ")) : words.person;
  if (!person || !access) return { status: "loading" };
  if (management?.deleted) return { status: "ready", subject, boxes: [hostedTask(management.boxes.delete)], opens: [management.boxes.delete.id] };
  const focused = FOCUSED.find(job => job === level.job);
  if (management && focused) {
    const task = management.boxes[focused];
    return { status: "ready", subject, boxes: [hostedTask(task)], opens: [task.id] };
  }
  // Change role sits with Access, after the sign-in it changes; its address opens it there even when it is blocked.
  const role = management && (management.canChangeRole || level.job === "role") && detail?.identity_and_access?.membership ? management.boxes.role : null;
  const accessWork = [...access.work, ...(role ? [role] : [])];
  if (level.job === "access") {
    const boxes = [...(management ? [management.summary] : []), { ...access.fact, presentation: "embedded" as const }, ...accessWork.map(box => box.kind === "task" && box.id === access.opens ? { ...box, presentation: "embedded" as const, footer: "host" as const } : box), ...access.records];
    return { status: "ready", subject, boxes, opens: access.opens ? [access.opens] : [] };
  }
  // Students lead with placement, status and courses. The native controllers
  // remain the same; optional fields and administration are disclosed by purpose.
  if (detail?.student_record) {
    const record = detail.student_record;
    const embedded = (box: Box): Box => ({ ...box, presentation: "embedded", ...(box.kind === "task" ? { summary: "compact" as const } : {}) });
    const ids = (names: string[]) => names.map(name => `${name}-${personId}`);
    const overview = ids(["status", "standing", "academic"]);
    const placement = ids(["student"]);
    const contact = [person.id, ...ids(["contact"])];
    const personal = ids(["personal", "note", "correct"]);
    const history = ids(["issuance", "imported"]);
    const extra = allowances.map(box => box.id);
    const accessBoxes = [access.fact, ...accessWork, ...access.records];
    const teachingBoxes = [...(management?.teachingAttention ? [management.boxes.teaching] : []), ...teaching];
    const arranged = overview.flatMap(id => studentRecord.filter(box => box.id === id));
    const boxes: Box[] = [
      ...(management ? [{ ...management.summary, label: studentWords.studentLabel } as Box] : []),
      ...arranged.map(embedded),
      { ...person, fold: "never", presentation: "embedded" },
      ...studentRecord.filter(box => !overview.includes(box.id)).map(box => embedded(box.kind === "fact" && placement.includes(box.id) ? { ...box, fold: "never" } : box)),
      ...accessBoxes.map(embedded), ...allowances.map(embedded), ...teachingBoxes.map(embedded),
    ];
    const allowanceList = allowances.find(box => box.kind === "list");
    const allowanceValue = allowanceList?.read.status === "ready" ? allowanceList.read.value : undefined;
    const allowanceSummary = allowanceValue !== undefined ? allowanceList?.state?.(allowanceValue)?.word : allowanceList?.read.status === "empty" ? studentWords.noExtraTime : undefined;
    const sections = [
      ...(teachingBoxes.length ? [{ id: "teaching", label: "Teaching", summary: [detail.faculty_profile?.title_role, detail.teaching_assignments ? teachingText.sections(detail.teaching_assignments.courses.length) : null].filter(Boolean).join(" · ") || undefined, boxIds: teachingBoxes.map(box => box.id), defaultOpen: Boolean(management?.teachingAttention) }] : []),
      { id: "person", label: studentWords.contactAndIdentity, summary: [detail.person.email, record.phone].filter(Boolean).join(" · ") || undefined, boxIds: contact },
      { id: "placement", label: studentWords.placementLabel, boxIds: placement },
      { id: "personal", label: studentWords.personalAndNotes, summary: record.internal_note?.note ? studentWords.noteRecorded : record.gender || record.citizenship_note ? studentWords.personalRecorded : studentWords.nothingRecorded, boxIds: personal },
      { id: "access", label: "Access", icon: ShieldCheck, summary: accessSummary(detail), boxIds: accessBoxes.map(box => box.id) },
      { id: "extra-time", label: "Extra time", summary: allowanceSummary || undefined, boxIds: extra },
      { id: "history", label: studentWords.recordHistory, summary: record.import_provenance?.state === "imported" ? studentWords.sourceAndIssuance : studentWords.issuanceSummary, boxIds: history },
    ];
    const opens = level.job === "edit" ? [person.id] : role && level.job === "role" ? [role.id] : [];
    return { status: "ready", subject, boxes, sections, opens };
  }
  // Teaching is the faculty record's work. Account administration is optional,
  // and the same native controllers remain mounted behind each disclosure.
  if (detail && (detail.faculty_profile || management?.teachingAttention || teaching.length)) {
    const embedded = (box: Box): Box => ({ ...box, presentation: "embedded", ...(box.kind === "task" ? { summary: "compact" as const } : {}) });
    const assignments = teaching.filter(box => box.id === `assign-${personId}`);
    const details = teaching.filter(box => box.id !== `assign-${personId}`);
    const attention = management?.teachingAttention ? [management.boxes.teaching] : [];
    const accessBoxes = [access.fact, ...accessWork, ...access.records];
    const boxes = [
      ...(management ? [{ ...management.summary, label: detail.faculty_profile && !details.some(box => box.kind === "task" && box.result.state === "unknown") ? teachingCopy.facultyRecord : teachingCopy.personRecord } as Box] : []),
      ...attention.map(embedded), ...assignments.map(embedded), ...details.map(embedded),
      { ...person, fold: "never" as const, presentation: "embedded" as const }, ...accessBoxes.map(embedded),
    ];
    const sections = [
      { id: "teaching", label: teachingCopy.teachingDetails, boxIds: details.map(box => box.id) },
      { id: "person", label: studentWords.contactAndIdentity, summary: detail.person.email || undefined, boxIds: [person.id] },
      { id: "access", label: "Access", summary: accessSummary(detail), boxIds: accessBoxes.map(box => box.id) },
    ];
    const opens = level.job === "edit" ? [person.id] : role && level.job === "role" ? [role.id] : [];
    return { status: "ready", subject, boxes, sections, opens };
  }
  const fact = access.fact as FactBox<PeopleDetailResponse>;
  const accessBoxes = [access.fact, ...accessWork, ...access.records];
  // An instructor who signs in without a profile: Add teaching profile, the same Task the Manage menu visits.
  const teachingBoxes = [...(management?.teachingAttention ? [management.boxes.teaching] : []), ...teaching];
  const embedded = (box: Box) => ({ ...box, presentation: "embedded" as const });
  const boxes = [...(management ? [management.summary] : []), { ...person, fold: "never" as const, presentation: "embedded" as const }, ...accessBoxes.map(embedded), ...teachingBoxes.map(embedded)];
  const sections = [
    { id: "person", label: "Person", icon: UserRound, summary: detail?.person.email || "Identity and contact", boxIds: [person.id] },
    { id: "access", label: "Access", icon: ShieldCheck, summary: detail ? fact.prose?.(detail) : undefined, boxIds: accessBoxes.map(box => box.id), defaultOpen: Boolean(detail) },
    // Teaching opens when it holds the record's attention: an instructor who signs in without a profile.
    ...(teachingBoxes.length ? [{ id: "teaching", label: "Teaching", icon: BookOpen, summary: detail?.faculty_profile?.title_role || "Teaching profile and assignments", boxIds: teachingBoxes.map(box => box.id), defaultOpen: Boolean(management?.teachingAttention) }] : []),
  ];
  // Edit and Change role open the record itself, with their section and work open (O1): no separate editor.
  const opens = level.job === "edit" ? [person.id] : role && level.job === "role" ? [role.id] : [];
  return { status: "ready", subject, boxes, sections, opens };
}
